import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { directoryApi, type CompanyInput, type PersonInput, type ProjectContactEntry, type ProjectDirectory } from '@/lib/apiClient';
import { Button, Modal } from '@/components';
import { DirectoryStyles } from './directoryStyles';
import { BookCompanyPicker, CompanyFields, PersonFields, emptyCompany } from './directoryFields';

/** A form value as sent: the same fields, '' for an empty one. */
const personOf = (p: ProjectContactEntry['person']): PersonInput => ({
  name: p.name, title: p.title ?? '', email: p.email ?? '', phone: p.phone ?? '', cell: p.cell ?? '', notes: p.notes ?? '',
});
const companyOf = (c: NonNullable<ProjectContactEntry['company']>): CompanyInput => ({
  name: c.name, kind: c.kind, phone: c.phone ?? '', address1: c.address1 ?? '', address2: c.address2 ?? '', city: c.city ?? '',
  state: c.state ?? '', postalCode: c.postalCode ?? '', website: c.website ?? '', notes: c.notes ?? '', divisions: [...c.divisions],
});
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Edit someone on the job (#598): their role and note here, and their entry in
 * the address book — which shows on every job they're on, as the dialog says.
 * One request, saved all or nothing: as three, a person saved before the company
 * or role was refused stayed changed on every job while the dialog said the save
 * had failed (Codex on #632). Only the parts that changed are included.
 */
export default function EditContactDialog({ projectId, directory, entry, onClose, onSaved }: {
  projectId: number;
  directory: ProjectDirectory;
  entry: ProjectContactEntry;
  onClose: () => void;
  /** With the job's directory as the save left it, so the list needn't ask again. */
  onSaved: (d: Pick<ProjectDirectory, 'team' | 'contacts'>) => void;
}) {
  const [role, setRole] = useState(entry.role);
  const [note, setNote] = useState(entry.note ?? '');
  const [person, setPerson] = useState<PersonInput>(() => personOf(entry.person));
  const [company, setCompany] = useState<CompanyInput | null>(() => (entry.company ? companyOf(entry.company) : null));
  // Which company they're at: kept, another one picked from the book (null = none),
  // or a new one entered here (`fresh`), which goes into the book. Moving someone
  // doesn't edit either company; the company's own fields show only while they
  // stay where they are.
  const [at, setAt] = useState<{ id: number; name: string } | null>(entry.company ? { id: entry.company.id, name: entry.company.name } : null);
  const [fresh, setFresh] = useState<CompanyInput | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const moved = !!fresh || (at?.id ?? null) !== (entry.company?.id ?? null);
  const personChanged = !same(person, personOf(entry.person)) || moved;
  const companyChanged = !moved && !!entry.company && !!company && !same(company, companyOf(entry.company));
  const noteChanged = note.trim() !== (entry.note ?? '');
  const jobChanged = role !== entry.role || noteChanged;
  const ready = !choosing && !!person.name.trim()
    && (fresh ? !!fresh.name.trim() : moved || !company || !!company.name.trim())
    && (personChanged || companyChanged || jobChanged);

  const save = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await directoryApi.update(projectId, entry.id, {
        // The role and note go only when changed here: left out, they're kept, so
        // an edit to someone's details alone neither puts back nor is refused over
        // a change made to the entry since (review). With each part sent goes what
        // it said when this opened, so a save can't put back someone else's change
        // either: the server refuses it instead (review).
        ...(role !== entry.role ? { role } : {}),
        ...(noteChanged ? { note: note.trim() || null } : {}),
        ...(jobChanged ? { entryVersion: entry.versions?.entry } : {}),
        ...(fresh || personChanged ? { personVersion: entry.versions?.person } : {}),
        // Their company goes only when it was changed here. Sent on every save, a
        // dialog opened before someone else moved them would move them back (review).
        ...(fresh ? { person, newCompany: fresh } : personChanged ? { person: moved ? { ...person, companyId: at?.id ?? null } : person } : {}),
        // With the company it's for, so it can't land on another if they've moved since (review).
        ...(companyChanged ? { company: company!, editingCompanyId: entry.company!.id, companyVersion: entry.versions?.company ?? undefined } : {}),
      });
      // The book changed: suggestions and searches kept from before are stale.
      if (personChanged || companyChanged) {
        void qc.invalidateQueries({ queryKey: ['directory-suggestions'] });
        void qc.invalidateQueries({ queryKey: ['directory-search'] });
      }
      onSaved(saved);
    } catch (e) {
      setError((e as Error).message || 'That wasn’t saved. Try again.');
      // Refused because the list moved on (someone took them off the job, say):
      // nothing was saved, so show the job as it is now behind the message, rather
      // than leave a contact there that every retry will fail on (review).
      void qc.invalidateQueries({ queryKey: ['project-contacts', projectId] });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Edit — ${entry.person.name}`} maxWidth={720}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => { void save(); }} isLoading={busy} disabled={!ready}>Save</Button>
        </>
      }>
      <DirectoryStyles />
      <div className="dir-form">
        <p className="dir-legend">On this job</p>
        <div className="dir-grid2">
          <label className="dir-field"><span>Role on this job</span>
            <select value={role} onChange={(e) => setRole(e.target.value)} disabled={busy}>
              {directory.roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </label>
          <label className="dir-field"><span>Note for this job</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} disabled={busy} />
          </label>
        </div>

        <p className="dir-legend">In the address book</p>
        <p className="dir-muted dir-small" style={{ margin: 0 }}>Changes here show on every job {entry.company ? 'this person and company are' : 'this person is'} on.</p>
        <PersonFields value={person} onChange={setPerson} disabled={busy} />

        <p className="dir-legend">Company</p>
        {choosing ? (
          <>
            <BookCompanyPicker directory={directory} onPick={(c) => { setAt(c); setFresh(null); setChoosing(false); }} />
            <div className="dir-row-actions">
              <button type="button" className="dir-act" onClick={() => setChoosing(false)}>Cancel</button>
              <button type="button" className="dir-act"
                // Back to a new company already started: keep what was typed, so a
                // look in the book first costs nothing (review).
                onClick={() => { setFresh((f) => f ?? emptyCompany('', entry.company?.kind ?? 'subcontractor')); setAt(null); setChoosing(false); }}>
                A new company
              </button>
              {(at || fresh) && <button type="button" className="dir-act" onClick={() => { setAt(null); setFresh(null); setChoosing(false); }}>No company</button>}
            </div>
          </>
        ) : (
          <div className="dir-chosen">
            <span>
              {fresh ? 'At a new company, added to the address book'
                : at ? <>At <strong>{at.name}</strong>{moved ? ' (moving there)' : ''}</>
                  : moved ? 'No company (moving off ' + entry.company!.name + ')' : 'No company'}
            </span>
            <button type="button" className="dir-act" disabled={busy} onClick={() => setChoosing(true)}>
              {at || fresh ? 'Change company' : 'Put at a company'}
            </button>
          </div>
        )}
        {fresh && !choosing && (
          <CompanyFields value={fresh} onChange={setFresh} directory={directory} disabled={busy} />
        )}
        {company && !moved && (
          <>
            <p className="dir-legend">{entry.company!.name}</p>
            <CompanyFields value={company} onChange={setCompany} directory={directory} disabled={busy} />
          </>
        )}
        {error && <div className="dir-error" role="alert">{error}</div>}
      </div>
    </Modal>
  );
}
